/**
 * EWSRiskFactorsList - Grid display of risk factors with badges
 *
 * Displays risk factors in a 2-column grid layout.
 * Each factor shows the name and a colored badge indicating the risk level.
 */

import React from 'react';
import { EWSRiskFactorsListProps, RiskLevel, getRiskLevelColor } from '../../../types/ews.types';
import { Tag } from 'equal-ds-ui';

/**
 * Risk level badge component.
 * Font: Helvetica Neue, Medium, 12px, tracking 2%
 */
const RiskBadge: React.FC<{ level: RiskLevel }> = ({ level }) => {
  const colors = getRiskLevelColor(level);

  return (
    <Tag
      size="md"
      className={`${colors.bg} ${colors.text} ${colors.border} border`}
    >
      {level}
    </Tag>
  );
};

export const EWSRiskFactorsList: React.FC<EWSRiskFactorsListProps> = ({ factors }) => {
  return (
    <div className="grid grid-cols-2 gap-1">
      {factors.map((factor, index) => (
        <div
          key={`${factor.name}-${index}`}
          className="px-4 py-4 bg-white border border-gray-400 rounded-lg"
        >
          {/* Name and Badge in separate rows, left aligned */}
          <div className="flex flex-col items-start gap-4">
            <span className="text-base font-medium text-gray-700">
              {factor.name}
            </span>
            <RiskBadge level={factor.level} />
          </div>
          {/* Description below if available */}
          {factor.description && (
            <div className="mt-3">
              <span className="text-sm text-gray-400 italic">
                {factor.description}
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default EWSRiskFactorsList;
